import {
  INSTALLMENT_STATUS_LABEL,
  PAYABLE_STATUS_LABEL,
  formatMoney,
  type PayableFilter,
  type Receivable,
  type ReceivableInstallmentRow,
  type ReceivableList,
} from "@cafegestor/shared";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useCan } from "@/app/me";
import {
  Alert,
  Badge,
  Button,
  Card,
  Field,
  Input,
  PageTitle,
  Select,
  Spinner,
  Table,
} from "@/components/ui";
import { EmptyState, ErrorState, KeyFacts, SummaryCard } from "@/components/patterns";
import { apiFetch, errorMessage } from "@/lib/api";
import { Pager, SaveError, SearchInput } from "../cadastros/widgets";
import { dateBR, dateTimeBR, useDocAction } from "../estoque/lib";

const KEY = ["financeiro", "contas-a-receber"];

const FILTER_LABEL: Record<PayableFilter, string> = {
  abertas: "Em aberto",
  vencidas: "Vencidas",
  a_vencer: "A vencer",
  pagas: "Pagas",
  canceladas: "Canceladas",
  todas: "Todas",
};

function StatusBadge({
  row,
}: {
  row: Pick<ReceivableInstallmentRow, "status" | "overdue" | "daysToDue">;
}) {
  if (row.status === "aberta" && row.overdue) return <Badge tone="error">vencida</Badge>;
  if (row.status === "aberta" && row.daysToDue <= 7)
    return <Badge tone="warn">vence em {row.daysToDue} dia(s)</Badge>;
  if (row.status === "paga") return <Badge tone="ok">recebida</Badge>;
  if (row.status === "cancelada") return <Badge>cancelada</Badge>;
  return <Badge>{INSTALLMENT_STATUS_LABEL[row.status].toLowerCase()}</Badge>;
}

export function ContasReceberPage() {
  const [situacao, setSituacao] = useState<PayableFilter>("abertas");
  const [q, setQ] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [offset, setOffset] = useState(0);
  // vindo do pedido de venda (?titulo=id): abre o título direto
  const [open, setOpen] = useState<string | null>(() =>
    typeof window === "undefined"
      ? null
      : new URLSearchParams(window.location.search).get("titulo"),
  );
  const params = new URLSearchParams({ situacao, limit: "50", offset: String(offset) });
  if (q.trim()) params.set("q", q.trim());
  if (from) params.set("from", from);
  if (to) params.set("to", to);
  const list = useQuery({
    queryKey: [...KEY, params.toString()],
    queryFn: () => apiFetch<ReceivableList>(`/financeiro/contas-a-receber?${params.toString()}`),
    placeholderData: (p) => p,
  });
  const s = list.data?.summary;
  const reset = () => setOffset(0);

  return (
    <div className="space-y-6">
      <PageTitle
        title="Contas a receber"
        description="Títulos gerados pela confirmação das vendas a prazo. Vendas em dinheiro, PIX e demais formas à vista ficam registradas no próprio pedido como recebidas e não aparecem aqui. Recebimentos (baixas) entram na fase do financeiro; aqui você acompanha vencimentos e renegocia datas."
      />
      {s && (
        <section
          aria-label="Indicadores"
          className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4"
        >
          <SummaryCard
            label="Total em aberto"
            value={formatMoney(s.openTotal)}
            hint={`${s.openCount} parcela(s)`}
            tone="info"
          />
          <SummaryCard
            label="Vencidas"
            value={formatMoney(s.overdueTotal)}
            hint={`${s.overdueCount} parcela(s)`}
            tone={s.overdueCount > 0 ? "error" : "neutral"}
            emphasize
          />
          <SummaryCard
            label="Vencem em 7 dias"
            value={formatMoney(s.next7DaysTotal)}
            hint="próximos sete dias"
            tone="warn"
          />
          <SummaryCard
            label="Parcelas em aberto"
            value={String(s.openCount)}
            hint={s.overdueCount > 0 ? `das quais ${s.overdueCount} vencida(s)` : "nenhuma vencida"}
            tone="ok"
          />
        </section>
      )}
      {open && <ReceivableDetail key={open} id={open} onClose={() => setOpen(null)} />}
      <Card>
        <div className="mb-4 flex flex-wrap items-end gap-3">
          <Field label="Pesquisar">
            <SearchInput
              placeholder="Cliente, título ou pedido"
              value={q}
              className="w-72 max-w-full"
              onChange={(v) => {
                setQ(v);
                reset();
              }}
            />
          </Field>
          <Field label="Situação">
            <Select
              className="w-40"
              value={situacao}
              onChange={(e) => {
                setSituacao(e.target.value as PayableFilter);
                reset();
              }}
            >
              {(Object.keys(FILTER_LABEL) as PayableFilter[]).map((f) => (
                <option key={f} value={f}>
                  {FILTER_LABEL[f]}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Vencimento de">
            <Input
              type="date"
              value={from}
              onChange={(e) => {
                setFrom(e.target.value);
                reset();
              }}
            />
          </Field>
          <Field label="até">
            <Input
              type="date"
              value={to}
              onChange={(e) => {
                setTo(e.target.value);
                reset();
              }}
            />
          </Field>
        </div>
        {list.error ? (
          <ErrorState message={errorMessage(list.error)} />
        ) : list.isPending ? (
          <Spinner />
        ) : list.data?.items.length === 0 ? (
          <EmptyState
            title="Nenhuma parcela neste filtro."
            description="Mude a situação, o período ou a pesquisa para ver outras parcelas."
          />
        ) : (
          <>
            <Table head={["Vencimento", "Cliente", "Título", "Pedido", "Valor", "Situação", ""]}>
              {list.data?.items.map((r) => (
                <tr key={r.id}>
                  <td
                    className={`px-3 py-2 whitespace-nowrap ${r.status === "aberta" && r.overdue ? "font-semibold text-destructive" : ""}`}
                  >
                    {dateBR(r.dueDate)}
                  </td>
                  <td className="px-3 py-2">{r.customerName}</td>
                  <td className="px-3 py-2 font-mono whitespace-nowrap">
                    {r.receivableNumber} · {r.number}/{r.installmentCount}
                  </td>
                  <td className="px-3 py-2 font-mono">{r.salesOrderNumber ?? "—"}</td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    {formatMoney(r.openAmount)}
                  </td>
                  <td className="px-3 py-2">
                    <StatusBadge row={r} />
                  </td>
                  <td className="px-3 py-2 text-right">
                    <Button size="sm" variant="ghost" onClick={() => setOpen(r.receivableId)}>
                      Abrir
                    </Button>
                  </td>
                </tr>
              ))}
            </Table>
            <p className="mt-3 text-sm">
              Total em aberto no filtro:{" "}
              <strong>{formatMoney(list.data?.filteredOpenTotal ?? "0")}</strong>
            </p>
            <Pager total={list.data?.total ?? 0} offset={offset} onOffset={setOffset} />
          </>
        )}
      </Card>
    </div>
  );
}

function ReceivableDetail({ id, onClose }: { id: string; onClose: () => void }) {
  const can = useCan();
  const receivable = useQuery({
    queryKey: [...KEY, "detalhe", id],
    queryFn: () => apiFetch<Receivable>(`/financeiro/contas-a-receber/${id}`),
  });
  const [editing, setEditing] = useState<string | null>(null);
  const [dueDate, setDueDate] = useState("");
  const save = useDocAction<
    { dueDate: string; version: number; installmentId: string },
    Receivable
  >({
    path: (b) => `/financeiro/contas-a-receber/parcelas/${b.installmentId}/vencimento`,
    method: "PUT",
    invalidate: [KEY],
    onDone: () => setEditing(null),
    successMessage: () => "Vencimento atualizado com sucesso.",
  });
  const p = receivable.data;
  if (receivable.error) return <Alert tone="error">{errorMessage(receivable.error)}</Alert>;
  if (!p) return <Spinner />;
  return (
    <Card
      title={`Título ${p.number} — ${p.customerName}`}
      actions={
        <Button size="sm" variant="secondary" onClick={onClose}>
          Fechar
        </Button>
      }
    >
      <div className="space-y-4 text-sm">
        <KeyFacts
          items={[
            { label: "Situação", value: PAYABLE_STATUS_LABEL[p.status] },
            { label: "Valor", value: formatMoney(p.totalAmount) },
            { label: "Emissão", value: dateBR(p.issueDate) },
            ...(p.salesOrderNumber
              ? [{ label: "Pedido de venda", value: p.salesOrderNumber }]
              : []),
            {
              label: "Criado",
              value: `${dateTimeBR(p.createdAt)} · ${p.createdByEmail ?? "—"}`,
            },
          ]}
        />
        <p>{p.description}</p>
        {p.status === "cancelado" && <Alert tone="warn">Cancelado. {p.cancelReason}</Alert>}
        <Table head={["Parcela", "Vencimento", "Valor", "Recebido", "Situação", ""]}>
          {p.installments.map((i) => (
            <tr key={i.id}>
              <td className="px-3 py-2">
                {i.number}/{p.installments.length}
              </td>
              <td className="px-3 py-2">
                {editing === i.id ? (
                  <div className="flex items-center gap-2">
                    <Input
                      type="date"
                      value={dueDate}
                      onChange={(e) => setDueDate(e.target.value)}
                    />
                    <Button
                      size="sm"
                      disabled={!dueDate || save.isPending}
                      onClick={() =>
                        save.mutate({ dueDate, version: i.version, installmentId: i.id })
                      }
                    >
                      Salvar
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => setEditing(null)}>
                      Cancelar
                    </Button>
                  </div>
                ) : (
                  dateBR(i.dueDate)
                )}
              </td>
              <td className="px-3 py-2 text-right">{formatMoney(i.amount)}</td>
              <td className="px-3 py-2 text-right">{formatMoney(i.paidAmount)}</td>
              <td className="px-3 py-2">
                {i.status === "aberta" && i.overdue ? (
                  <Badge tone="error">vencida</Badge>
                ) : (
                  <Badge tone={i.status === "paga" ? "ok" : "neutral"}>
                    {INSTALLMENT_STATUS_LABEL[i.status]}
                  </Badge>
                )}
              </td>
              <td className="px-3 py-2 text-right">
                {i.status === "aberta" &&
                  p.status === "aberto" &&
                  can("financeiro.titulo.lancar") &&
                  editing !== i.id && (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => {
                        setEditing(i.id);
                        setDueDate(i.dueDate);
                      }}
                    >
                      Alterar vencimento
                    </Button>
                  )}
              </td>
            </tr>
          ))}
        </Table>
        <SaveError error={save.error} />
      </div>
    </Card>
  );
}
